import React from 'react';
import { X, Download, Printer } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
interface ReportPreviewProps {
  data: any;
  onClose: () => void;
}
export function ReportPreview({
  data,
  onClose
}: ReportPreviewProps) {
  const handlePrint = () => {
    window.print();
  };
  return <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4" data-id="element-68">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col" data-id="element-69">
        {/* Header - Non stampabile */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 print:hidden" data-id="element-70">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-71">
            Anteprima Report
          </h2>
          <div className="flex items-center gap-2" data-id="element-72">
            <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 bg-[#1388ad] text-white rounded-lg hover:bg-[#0f6b8a] transition-colors" data-id="element-73">
              <Download className="w-4 h-4" data-id="element-74" />
              Scarica PDF
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-75">
              <X className="w-5 h-5 text-gray-600" data-id="element-76" />
            </button>
          </div>
        </div>
        {/* Report Content - Stampabile */}
        <div className="flex-1 overflow-y-auto p-8" data-id="element-77">
          <div id="report-content" className="max-w-4xl mx-auto" data-id="element-78">
            {/* Report Header */}
            <div className="mb-8 pb-6 border-b-2 border-gray-200" data-id="element-79">
              <div className="flex items-start justify-between mb-4" data-id="element-80">
                <div data-id="element-81">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-82">
                    {data.title || 'Report Edares'}
                  </h1>
                  <p className="text-sm text-gray-500" data-id="element-83">
                    Generato il{' '}
                    {new Date().toLocaleDateString('it-IT', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                  })}
                  </p>
                </div>
                <div className="text-right" data-id="element-84">
                  <div className="text-2xl font-bold text-[#1388ad] mb-1" data-id="element-85">
                    EDARES
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-86">
                    Real Estate Intelligence
                  </p>
                </div>
              </div>
            </div>
            {/* Executive Summary */}
            {data.summary && <div className="mb-8" data-id="element-87">
                <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-88">
                  Riepilogo Esecutivo
                </h2>
                <div className="bg-gray-50 p-6 rounded-lg" data-id="element-89">
                  <p className="text-gray-700 leading-relaxed" data-id="element-90">
                    {data.summary}
                  </p>
                </div>
              </div>}
            {/* KPI Cards */}
            {data.kpis && data.kpis.length > 0 && <div className="mb-8" data-id="element-91">
                <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-92">
                  Metriche Chiave
                </h2>
                <div className="grid grid-cols-2 gap-4" data-id="element-93">
                  {data.kpis.map((kpi: any, index: number) => <div key={index} className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-94">
                      <p className="text-sm text-gray-600 mb-1" data-id="element-95">{kpi.label}</p>
                      <p className="text-2xl font-bold text-gray-900" data-id="element-96">
                        {kpi.value}
                      </p>
                      {kpi.change && <span className="text-xs text-green-600 font-medium" data-id="element-97">
                          {kpi.change}
                        </span>}
                    </div>)}
                </div>
              </div>}
            {/* Charts */}
            {data.charts && data.charts.length > 0 && <div className="mb-8 space-y-8" data-id="element-98">
                {data.charts.map((chart: any, index: number) => <div key={index} className="break-inside-avoid" data-id="element-99">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-100">
                      {chart.title}
                    </h2>
                    <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-101">
                      <ResponsiveContainer width="100%" height={300} data-id="element-102">
                        {chart.type === 'line' && <LineChart data={chart.data} data-id="element-103">
                            <CartesianGrid strokeDasharray="3 3" data-id="element-104" />
                            <XAxis dataKey={chart.xKey || 'month'} data-id="element-105" />
                            <YAxis data-id="element-106" />
                            <Tooltip data-id="element-107" />
                            <Legend data-id="element-108" />
                            {chart.lines?.map((line: any, i: number) => <Line key={i} type="monotone" dataKey={line.key} stroke={line.color} name={line.name} data-id="element-109" />)}
                          </LineChart>}
                        {chart.type === 'bar' && <BarChart data={chart.data} data-id="element-110">
                            <CartesianGrid strokeDasharray="3 3" data-id="element-111" />
                            <XAxis dataKey={chart.xKey || 'month'} data-id="element-112" />
                            <YAxis data-id="element-113" />
                            <Tooltip data-id="element-114" />
                            <Legend data-id="element-115" />
                            {chart.bars?.map((bar: any, i: number) => <Bar key={i} dataKey={bar.key} fill={bar.color} name={bar.name} data-id="element-116" />)}
                          </BarChart>}
                        {chart.type === 'pie' && <PieChart data-id="element-117">
                            <Pie data={chart.data} cx="50%" cy="50%" labelLine={false} label={entry => `${entry.name} ${(entry.value / chart.data.reduce((sum: number, item: any) => sum + item.value, 0) * 100).toFixed(0)}%`} outerRadius={100} fill="#8884d8" dataKey="value" data-id="element-118">
                              {chart.data.map((entry: any, i: number) => <Cell key={`cell-${i}`} fill={entry.color || '#1388ad'} data-id="element-119" />)}
                            </Pie>
                            <Tooltip data-id="element-120" />
                          </PieChart>}
                      </ResponsiveContainer>
                    </div>
                  </div>)}
              </div>}
            {/* Tables */}
            {data.tables && data.tables.length > 0 && <div className="mb-8 space-y-8" data-id="element-121">
                {data.tables.map((table: any, index: number) => <div key={index} className="break-inside-avoid" data-id="element-122">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-123">
                      {table.title}
                    </h2>
                    <div className="overflow-x-auto" data-id="element-124">
                      <table className="w-full border-collapse border border-gray-200" data-id="element-125">
                        <thead data-id="element-126">
                          <tr className="bg-gray-50" data-id="element-127">
                            {table.headers.map((header: string, i: number) => <th key={i} className="border border-gray-200 px-4 py-2 text-left text-sm font-semibold text-gray-900" data-id="element-128">
                                {header}
                              </th>)}
                          </tr>
                        </thead>
                        <tbody data-id="element-129">
                          {table.rows.map((row: any[], i: number) => <tr key={i} className="hover:bg-gray-50" data-id="element-130">
                              {row.map((cell, j) => <td key={j} className="border border-gray-200 px-4 py-2 text-sm text-gray-700" data-id="element-131">
                                  {cell}
                                </td>)}
                            </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </div>)}
              </div>}
            {/* Insights */}
            {data.insights && data.insights.length > 0 && <div className="mb-8" data-id="element-132">
                <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-133">
                  Insights e Raccomandazioni
                </h2>
                <div className="space-y-4" data-id="element-134">
                  {data.insights.map((insight: string, index: number) => <div key={index} className="bg-blue-50 border-l-4 border-[#1388ad] p-4 rounded-r-lg" data-id="element-135">
                      <p className="text-gray-700" data-id="element-136">{insight}</p>
                    </div>)}
                </div>
              </div>}
            {/* Footer */}
            <div className="mt-12 pt-6 border-t border-gray-200 text-center text-sm text-gray-500" data-id="element-137">
              <p data-id="element-138">© {new Date().getFullYear()} Edares - Report confidenziale</p>
            </div>
          </div>
        </div>
      </div>
      {/* Print Styles */}
      <style data-id="element-139">{`
        @media print {
          body * {
            visibility: hidden;
          }
          #report-content,
          #report-content * {
            visibility: visible;
          }
          #report-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .print\\:hidden {
            display: none !important;
          }
          @page {
            margin: 2cm;
          }
        }
      `}</style>
    </div>;
}